import { Cart, Order } from '@pylot-data/pylotschema'
import { reportSynonymConversion } from '@pylot-data/hooks/search/synonymConversion'

/**
 * Convierte un carrito al formato de contexto de Adobe Storefront Events
 */
export const getAdobeCartContext = (cart: Cart | null) => {
    if (!cart) return null

    return {
        id: cart.id || '',
        totalQuantity: cart.total_quantity || 0,
        prices: {
            subtotalExcludingTax: {
                value: cart.prices?.subtotal_excluding_tax?.value || 0,
                currency: cart.prices?.subtotal_excluding_tax?.currency || 'CLP'
            },
            subtotalIncludingTax: {
                value: cart.prices?.subtotal_including_tax?.value || 0,
                currency: cart.prices?.subtotal_including_tax?.currency || 'CLP'
            }
        },
        items:
            cart.items?.map((item: any) => ({
                id: item.uid || '',
                quantity: item.quantity || 0,
                prices: {
                    price: {
                        value: item.prices?.price?.value || 0,
                        currency: item.prices?.price?.currency || 'CLP'
                    }
                },
                product: {
                    sku: item.product?.sku || '',
                    name: item.product?.name || '',
                    url: item.product?.url_key || '',
                    imageUrl: item.product?.small_image?.url || ''
                }
            })) || []
    }
}

/**
 * Convierte un pedido (y el carrito actual) al formato de contexto de Adobe Storefront Events
 */
export const getAdobeOrderContext = (order: Order, cart: Cart | null) => {
    if (!order) return null

    return {
        orderId: order.order_number, // En Pylot solemos usar el increment_id como ID principal
        orderNumber: order.order_number,
        appliedCouponCode: cart?.applied_coupons?.[0]?.code || '',
        email: cart?.email || '',
        grandTotal: cart?.prices?.grand_total?.value || 0,
        subtotalExcludingTax: cart?.prices?.subtotal_excluding_tax?.value || 0,
        subtotalIncludingTax: cart?.prices?.subtotal_including_tax?.value || 0,
        shippingExcludingTax:
            cart?.shipping_addresses?.[0]?.selected_shipping_method?.amount
                ?.value || 0,
        shippingIncludingTax:
            cart?.shipping_addresses?.[0]?.selected_shipping_method?.amount
                ?.value || 0,
        taxAmount:
            cart?.prices?.applied_taxes?.reduce(
                (acc, tax) => acc + (tax?.amount?.value || 0),
                0
            ) || 0,
        shippingMethod:
            cart?.shipping_addresses?.[0]?.selected_shipping_method
                ?.carrier_title || '',
        paymentMethod: cart?.selected_payment_method?.title || '',
        orderItems:
            cart?.items?.map((item: any) => ({
                sku: item?.product?.sku || '',
                name: item?.product?.name || '',
                quantity: item?.quantity || 0,
                price: item?.prices?.price?.value || 0,
                currency: item?.prices?.price?.currency || 'CLP'
            })) || []
    }
}

/**
 * Actualiza el contexto de resultados de búsqueda para atribución (Add to Cart / Place Order)
 * basándose en el último ID de búsqueda guardado en sesión.
 */
export const updateAdobeSearchContext = (mse: any) => {
    if (!mse || typeof window === 'undefined') {
        console.log(
            '[LiveSearch][searchContext] updateAdobeSearchContext omitido:',
            { hasMse: Boolean(mse), hasWindow: typeof window !== 'undefined' }
        )
        return
    }

    try {
        const searchId = sessionStorage.getItem('lastSearchRequestId')
        const searchUnitId =
            sessionStorage.getItem('lastSearchUnitId') ?? 'search-bar'

        console.log('[LiveSearch] updateAdobeSearchContext:', {
            searchId,
            searchUnitId
        })

        if (searchId) {
            const currentResults = mse.context.getSearchResults() ?? {
                units: []
            }
            const unitIndex = currentResults.units.findIndex(
                (unit: any) => unit.searchUnitId === searchUnitId
            )

            if (unitIndex < 0) {
                currentResults.units.push({
                    searchUnitId,
                    searchRequestId: searchId,
                    products: [],
                    categories: [],
                    suggestions: [],
                    page: 1,
                    perPage: 20,
                    facets: []
                })
            } else {
                currentResults.units[unitIndex].searchRequestId = searchId
            }

            mse.context.setSearchResults(currentResults)
            console.log(
                '[LiveSearch][searchContext] setSearchResults aplicado; unidades:',
                currentResults.units?.length,
                'searchRequestId en unidad activa:',
                unitIndex < 0
                    ? currentResults.units[currentResults.units.length - 1]
                          ?.searchRequestId
                    : currentResults.units[unitIndex]?.searchRequestId
            )
            console.log(
                '[LiveSearch] searchResults context updated for attribution'
            )
        } else {
            console.log('[LiveSearch] No searchId found in sessionStorage')
        }
    } catch (error) {
        console.warn('[LiveSearch] Error updating search context:', error)
    }
}

/**
 * Emite `addToCart` en MSE con atribución Live Search: contexto de búsqueda
 * (`updateAdobeSearchContext`) y carrito vía `mse.context.setShoppingCart` antes de `publish`.
 */
export const publishAddToCartMse = (mse: any, cart: Cart | null) => {
    if (typeof window === 'undefined') {
        console.log(
            '[LiveSearch][ATC] omitido: no hay window (SSR o entorno sin DOM)'
        )
        return
    }
    if (!mse) {
        console.warn(
            '[LiveSearch][ATC] omitido: window.magentoStorefrontEvents no está definido (¿SDK/collect no cargados?)'
        )
        return
    }

    const cartCtx = getAdobeCartContext(cart)
    if (!cartCtx) {
        console.warn(
            '[LiveSearch][ATC] omitido: carrito nulo o sin contexto Adobe válido',
            {
                rawCartId: cart?.id ?? null
            }
        )
        return
    }

    try {
        console.log('[LiveSearch][ATC] inicio pipeline MSE addToCart', {
            cartId: cartCtx.id,
            totalQuantity: cartCtx.totalQuantity,
            lineItems: cartCtx.items?.length ?? 0
        })
        console.log(
            '[LiveSearch][ATC] paso 1/3: updateAdobeSearchContext (searchResults + sessionStorage)'
        )
        updateAdobeSearchContext(mse)
        console.log('[LiveSearch][ATC] paso 2/3: mse.context.setShoppingCart', {
            cartId: cartCtx.id,
            totalQuantity: cartCtx.totalQuantity
        })
        mse.context.setShoppingCart(cartCtx)
        console.log(
            '[LiveSearch][ATC] paso 3/3: mse.publish.addToCart() (sin argumentos; lee contexto)'
        )
        mse.publish.addToCart()
        // Feed behavior_score when this cart came from a synonym-driven search.
        void reportSynonymConversion('ADD_TO_CART')
        console.log(
            '[LiveSearch][ATC] hecho: publish.addToCart ejecutado; revisar red tp2 / Snowplow si aplica'
        )
    } catch (e) {
        console.warn('[LiveSearch][ATC] error en pipeline addToCart:', e)
    }
}
